import React, { useState } from 'react'

import {
  ArrowUturnRightIcon,
  CheckIcon,
  PaperClipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { format as formatDate } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import _startCase from 'lodash/startCase'
import Image from 'next/legacy/image'

import LoadingIcon from '../LoadingIcon'
import Spinner from '../Spinner'
import generic from '@/assets/file-icons/generic-svgrepo-com.svg'
import { MessageType } from '@/components/Message/comboBoxMapper'
import Avatar from '@/components/WorkQueuesContent/components/avatar'
import { handleOpenFile } from '@/features/FileManager/utilities'
import { ApplicationProfile } from '@/types/unlock'
import { IMessage } from '@/types/unlock'
import { formatToFile } from '@/utils/formatToFile'
import toProperCase from '@/utils/toProperCase'

interface IMessageBodyProps extends IMessage {
  deleteLoading?: boolean
  handleDeleteMessage?: () => void
  setIsReplies?: (value: boolean) => void
  isReplies?: boolean
  hasReplies?: boolean
  setIsMessage?: (value: boolean) => void
  isMessage?: boolean
  applicants?: ApplicationProfile[]
  nonApplicants?: ApplicationProfile[]
}

export default function MessageBody(props: IMessageBodyProps) {
  const {
    from_profile,
    modified_at,
    body,
    document,
    parent_message,
    read_at,
    type,
    handleDeleteMessage,
    deleteLoading,
    setIsReplies,
    isReplies,
    hasReplies,
    setIsMessage,
    isMessage,
    read_at: readAt,
    applicants = [],
    nonApplicants = [],
    to_profile,
    to_profile_detail,
  } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFileClicked = async (file) => {
    // Opens a file in a new tab
    // Add downloadPath attribute for handleOpenFile compatibility
    const tempFile = { downloadPath: file.download_url, ...file }
    handleOpenFile(tempFile, setIsLoading)
  }

  const gmtDate = utcToZonedTime(new Date(modified_at), 'GMT')

  const isApplicant = applicants?.some(
    (person) => person.profile.id === to_profile && person.position === 1
  )

  const isCoApplicant = applicants?.some(
    (person) => person.profile.id === to_profile && person.position > 1
  )

  const isNonApplicant = nonApplicants?.some((person) => person.profile.id === to_profile)

  const profileType = isApplicant
    ? 'Applicant'
    : isCoApplicant
      ? 'Co-Applicant'
      : isNonApplicant
        ? 'Non-Applicant'
        : null

  return (
    <>
      <div className={`flex justify-between px-4 py-3 rounded-t-2xl `}>
        <div className='flex flex-row gap-2 items-center'>
          <div>
            <Avatar user={from_profile?.user} />
          </div>
          <p className='text-sm card-title'>
            {toProperCase(
              from_profile?.user?.data.full_name ||
                from_profile?.user?.first_name ||
                from_profile?.user?.last_name ||
                'Anon'
            )}
          </p>
        </div>
        <div className='flex flex-row gap-2'>
          <p className='flex items-center text-[10px] font-light'>
            {formatDate(gmtDate, 'MM/dd/yyyy h:mm a')}
          </p>
          <div className='flex flex-row gap-0 items-center'>
            {readAt ? (
              <div
                className='flex tooltip tooltip-left'
                data-tip='Read'
              >
                <CheckIcon
                  data-tip
                  data-for='read'
                  className='w-3 text-success'
                />
                <CheckIcon
                  data-tip
                  data-for='read'
                  className='w-3 ml-[-4px] text-success'
                />
              </div>
            ) : (
              <div
                className='flex tooltip tooltip-left'
                data-tip='Unread'
              >
                <CheckIcon
                  data-tip
                  data-for='unread'
                  className={'w-3'}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='flex flex-col w-full px-4 pt-0 py-3 gap-2'>
        {props?.to_profile_detail && profileType && (
          <div className='text-sm font-semibold my-1'>
            <span className='text-gray-600'>{profileType}:</span>
            <span className='ml-1'>{to_profile_detail?.full_name}</span>
          </div>
        )}
        <p
          className='text-[13px] break-words whitespace-pre-wrap'
          dangerouslySetInnerHTML={{ __html: body }}
        />
        {document && (
          <div className='w-2/5'>
            <a
              onClick={() => handleFileClicked(document)}
              target='_blank'
              rel='noreferrer'
              className='w-full flex flex-row p-1 cursor-pointer items-center'
            >
              <div className='p-[1px] flex justify-center mr-2'>
                <div className='w-[20px] shrink-0'>
                  <Image
                    layout={'responsive'}
                    src={formatToFile.get(document?.original_filename.split('.')[1]) || generic}
                    alt={'JPG file'}
                  />
                </div>
                {isLoading && (
                  <div className='flex justify-center items-center'>
                    <Spinner size={4} />
                  </div>
                )}
              </div>
              <p className='text-xs whitespace-nowrap text-ellipsis overflow-hidden'>
                {document?.original_filename}
              </p>
            </a>
          </div>
        )}
        {type === MessageType.REQUEST_DOCUMENT && (
          <div className='flex flex-row items-center text-[10px] justify-between font-bold'>
            <div className='flex flex-row gap-1 items-center'>
              {type === MessageType.REQUEST_DOCUMENT && (
                <>
                  <PaperClipIcon className='w-5' />
                  <p>Doc Requested</p>
                </>
              )}
            </div>
          </div>
        )}
        {setIsMessage && !isMessage && (
          <div className='flex flex-row justify-end items-center gap-2 pb-2'>
            {parent_message && (
              <div className='text-xs font-bold'>{read_at ? 'Read' : 'Unread'}</div>
            )}
            <div
              className='flex flex-row gap-1 text-xs cursor-pointer tooltip tooltip-left'
              data-tip='Reply'
              onClick={() => {
                setIsMessage(true)
              }}
            >
              <ArrowUturnRightIcon className='w-4' />
            </div>
          </div>
        )}
        {!parent_message && (
          <>
            {(hasReplies ||
              props.type === 'request-document' ||
              props.type === 'text' ||
              props.type === 'request-esignature') && (
              <div className='flex flex-row justify-end gap-2 pb-2 items-center'>
                {hasReplies && (
                  <div
                    className='flex flex-row gap-1 text-xs cursor-pointer'
                    onClick={() => {
                      setIsReplies(!isReplies)
                    }}
                  >
                    <div className='underline'>{!isReplies ? 'View Replies' : 'Hide Replies'}</div>
                    <ArrowUturnRightIcon className='w-4' />
                  </div>
                )}
                {(props.type === 'request-document' ||
                  props.type === 'text' ||
                  props.type === 'request-esignature') && (
                  <div
                    className='cursor-pointer'
                    onClick={handleDeleteMessage}
                  >
                    <p className='flex gap-2 text-xs font-bold hover:underline'>
                      <div
                        className='tooltip tooltip-left font-normal'
                        data-tip='Delete Message'
                      >
                        <TrashIcon className='w-4 cursor-pointer' />
                      </div>
                      {deleteLoading && (
                        <LoadingIcon
                          height={16}
                          width={16}
                        />
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
